import React, { createContext, useContext, useState, ReactNode } from 'react';
import { QuickscanData, QuickscanResults, QuickscanContextType } from '@/types/quickscan';
import {
  CO2_GRID_FACTOR_KG_KWH,
  GRID_IMPORT_TARIFF_EUR_KWH,
} from '@/features/laadplein-designer/lib/methodiek/constants';
import {
  QUICKSCAN_PRIORITIES,
  defaultQuickscanPriorities,
} from '@/lib/quickscan-priorities';

const initialData: QuickscanData = {
  businessType: '',
  employeeCount: '',
  monthlyEnergyCosts: 1000,
  estimatedPvGeneration: 10000, // Default 10,000 kWh per year
  estimatedKwhUsage: 15000, // Default 15,000 kWh per year
  connectionCapacityDelivery: 40, // Default 40 kW
  connectionCapacityReturn: 40, // Default 40 kW
  energyPeakHours: [],
  currentSolutions: [],
  expansionPlans: [],
  expansionDetails: {},
  // De vier schuifjes staan bij het openen op 5. Die stand hoort in de state en
  // niet alleen in beeld: eerder werd er pas geschreven zodra er één verschoof,
  // waardoor een bezoeker die ze liet staan nul prioriteiten inzond.
  priorities: defaultQuickscanPriorities()
};

const QuickscanContext = createContext<QuickscanContextType | undefined>(undefined);

export const useQuickscan = () => {
  const context = useContext(QuickscanContext);
  if (!context) {
    throw new Error('useQuickscan must be used within a QuickscanProvider');
  }
  return context;
};

export const QuickscanProvider = ({ children }: { children: ReactNode }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const [activeExpansionPlan, setActiveExpansionPlan] = useState<string | null>(null);
  const [data, setData] = useState<QuickscanData>(initialData);
  const [results, setResults] = useState<QuickscanResults | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const updateData = (field: keyof QuickscanData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const updateExpansionDetails = (planType: string, details: any) => {
    setData(prev => ({
      ...prev,
      expansionDetails: {
        ...prev.expansionDetails,
        [planType]: details
      }
    }));
  };

  const skipSubScreen = () => {
    console.log('Skipping sub-screen for:', activeExpansionPlan);
    // Move to next expansion plan or finish expansion details
    if (activeExpansionPlan) {
      const currentIndex = data.expansionPlans.indexOf(activeExpansionPlan);
      const remainingPlans = data.expansionPlans.slice(currentIndex + 1);
      const uncompletedPlans = remainingPlans.filter(plan => !data.expansionDetails[plan]);
      
      if (uncompletedPlans.length > 0) {
        setActiveExpansionPlan(uncompletedPlans[0]);
        setSubStep(1);
        return;
      } else {
        setActiveExpansionPlan(null);
        setSubStep(0);
      }
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 7));
  };

  const skipStep = () => {
    console.log('Skipping step:', currentStep);
    nextStep();
  };

  const nextStep = () => {
    if (currentStep === 5 && data.expansionPlans.length > 0) {
      // Check if we need to show sub-screens
      const uncompletedPlans = data.expansionPlans.filter(plan => !data.expansionDetails[plan]);
      if (uncompletedPlans.length > 0 && !activeExpansionPlan) {
        setActiveExpansionPlan(uncompletedPlans[0]);
        setSubStep(1);
        return;
      }
    }
    
    if (activeExpansionPlan) {
      // Move to next expansion plan or finish expansion details
      const currentIndex = data.expansionPlans.indexOf(activeExpansionPlan);
      const uncompletedPlans = data.expansionPlans.slice(currentIndex + 1).filter(plan => !data.expansionDetails[plan]);
      
      if (uncompletedPlans.length > 0) {
        setActiveExpansionPlan(uncompletedPlans[0]);
        setSubStep(1);
        return;
      } else {
        setActiveExpansionPlan(null);
        setSubStep(0);
      }
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 7));
  };

  const prevStep = () => {
    if (subStep > 0) {
      setSubStep(0);
      setActiveExpansionPlan(null);
      return;
    }
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const calculateResults = () => {
    // Simple calculation logic - can be enhanced
    const baseScore = 60;
    const costMultiplier = Math.min(data.monthlyEnergyCosts / 2000, 2);
    const solutionsBonus = data.currentSolutions.length * 5;
    // Delen door het aantal prioriteiten, niet door een los ingetypte 4, zodat
    // de bonus meebeweegt als de lijst ooit langer wordt.
    const priorityBonus =
      Object.values(data.priorities).reduce((sum, val) => sum + val, 0) / QUICKSCAN_PRIORITIES.length;
    
    const energyPotentialScore = Math.min(baseScore + (costMultiplier * 15) + solutionsBonus + priorityBonus, 100);
    const yearlyAnnualSavings = data.monthlyEnergyCosts * 12 * 0.2; // 20% savings estimate
    // Euro's besparing -> kWh -> kg CO2 -> ton, via de Greenvision-methodiek.
    // De oude formule vermenigvuldigde euro's rechtstreeks met 0,0004 en noemde
    // het tonnen; die stap van geld naar energie ontbrak, waardoor het cijfer
    // ruim 8x te laag uitkwam.
    const kwhSaved = yearlyAnnualSavings / GRID_IMPORT_TARIFF_EUR_KWH;
    const co2Reduction = (kwhSaved * CO2_GRID_FACTOR_KG_KWH) / 1000; // ton/jaar
    const roiMonths = Math.max(12, 36 - (energyPotentialScore / 10));

    setResults({
      energyPotentialScore: Math.round(energyPotentialScore),
      yearlyAnnualSavings: Math.round(yearlyAnnualSavings),
      co2Reduction: Math.round(co2Reduction * 10) / 10,
      roiMonths: Math.round(roiMonths),
      recommendations: generateRecommendations(data)
    });
  };

  const generateRecommendations = (data: QuickscanData): string[] => {
    const recommendations = [];
    
    if (data.monthlyEnergyCosts > 2000) {
      recommendations.push("Energieopslagsysteem voor piekvraag");
    }
    if (!data.currentSolutions.includes('solar')) {
      recommendations.push("Zonnepanelen installatie");
    }
    if (data.energyPeakHours.length > 4) {
      recommendations.push("Smart Energy Management System");
    }
    
    return recommendations;
  };

  return (
    <QuickscanContext.Provider value={{
      currentStep,
      subStep,
      activeExpansionPlan,
      data,
      results,
      submissionId,
      updateData,
      updateExpansionDetails,
      nextStep,
      prevStep,
      skipSubScreen,
      skipStep,
      calculateResults,
      setSubmissionId
    }}>
      {children}
    </QuickscanContext.Provider>
  );
};
